import { Delete, Download, FileUpload } from '@mui/icons-material';
import {
  ListItemIcon,
  MenuItem,
  useTheme,
  useMediaQuery,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { deleteFile, getFilesByEntityID } from 'api';
import { ListItemMenuButton } from 'components/buttons/ListItemMenuButton';
import FormFrame from 'components/layout/FormFrame';
import PhotoViewer from 'components/layout/PhotoViewer';
import MobilePanelList from 'components/lists/MobilePanelList';
import { TAB_LAYOUT_HEADER_HEIGHT } from 'components/layout/FormTabNavigation';
import MRTableToolbar from 'components/tables/PanelTableToolbar';
import { AppContext } from 'context/appContext';
import { FileContext } from 'context/fileUploadContext';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import dayjs from 'dayjs';
import {
  TableType,
  useDefaultTableOptions,
} from 'hooks/useDefaultTableOptions';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useOutletContext, useRevalidator } from 'react-router';
import {
  EntityTypes,
  FileMetadata,
  PanelRootContext,
  Property,
  ImageListItemData,
  FileType,
} from 'types';
import { capitalize, getFileTypeForFile, MAX_FILE_SIZE_BYTES } from 'utils';
interface PropertyFilesLoaderData {
  _files: Promise<FileMetadata[]>;
}
interface PropertyFilesRouteParams {
  params: {
    propertyID?: string | undefined;
    groupID?: string | undefined;
  };
}
export const propertyFilesLoader = ({
  params,
}: PropertyFilesRouteParams): PropertyFilesLoaderData => {
  if (params.propertyID) {
    return {
      _files: getFilesByEntityID(
        EntityTypes.Property,
        Number(params.propertyID),
      ),
    };
  }

  if (params.groupID) {
    return {
      _files: getFilesByEntityID(
        EntityTypes.PropertyGroup,
        Number(params.groupID),
      ),
    };
  }

  return { _files: Promise.resolve([]) };
};

export const PropertyFiles = (): ReactNode => {
  const { entity } = useOutletContext<PanelRootContext<Property>>();
  const { t } = useTranslation();
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const { _files } = useLoaderData() as PropertyFilesLoaderData;
  const revalidator = useRevalidator();
  const [selected, setSelected] = useState<ImageListItemData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const { state } = useContext(AppContext);
  const {
    addFiles,
    files: uploadedFiles,
    clearUploaded,
  } = useContext(FileContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const displayToast = useContext(ToastContext);
  const canUpload = state.entityPermissions[EntityTypes.Property].update;

  useEffect(() => {
    const awaitData = async () => {
      const data = await _files;
      setFiles(data);
      setIsLoading(false);
    };
    awaitData();
  }, [_files]);

  // after files upload, revalidate so it shows in list
  useEffect(() => {
    if (isSubmitting) {
      if (!uploadedFiles.some((file) => !file.uploaded)) {
        clearUploaded();
        setIsSubmitting(false);
        revalidator.revalidate();
      }
    }
  }, [uploadedFiles, isSubmitting]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    const validFiles = Array.from(files).filter((file) => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: `File "${file.name}" exceeds the 10 MB size limit`,
        });
        return false;
      }
      return true;
    });
    if (validFiles.length === 0) return;
    setIsSubmitting(true);
    addFiles(
      validFiles.map((file: File) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
          metadata: {
            createdByUserID: state.user.id,
            tenantID: state.tenant.id,
            entityType: EntityTypes.Property,
            entityID: Number(entity.id),
            fileType: getFileTypeForFile(file),
            data: {
              title: file.name,
              description: '',
            },
          },
          uploaded: false,
        }),
      ),
    );
  };
  const handleDownloadClick = (file: FileMetadata) => {
    fetch(file.downloadFileURL || '').then((response) => {
      response.blob().then((blob) => {
        // Creating new object of PDF file
        const fileURL = window.URL.createObjectURL(blob);

        // Setting various property values
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.download = `${file.data.title}`;
        alink.click();
      });
    });
  };
  const handleRemoveClick = async (file: FileMetadata) => {
    setIsSubmitting(true);
    await deleteFile(file.fileKey || '');
    setIsSubmitting(false);
    revalidator.revalidate();
  };
  const handleRowClick = (file: FileMetadata) => {
    // check if file extension is an image
    if (file.fileType === FileType.Image) {
      setSelected({
        img: file.downloadFileURL || '',
        title: file.data.title,
        type: file.fileType,
      });
    }
  };
  const handleCloseImage = () => {
    setSelected(null);
  };
  const columns: MRT_ColumnDef<FileMetadata>[] = [
    {
      header: t('file.name'),
      accessorKey: 'data.title',
    },
    {
      header: t('file.type'),
      accessorKey: 'fileType',
      Cell: ({ renderedCellValue }) => {
        return capitalize(renderedCellValue);
      },
    },
    {
      header: t('file.created'),
      accessorKey: 'created',
      accessorFn: (row) => new Date(row.created || ''),
      Cell: ({ cell }) => {
        return dayjs(cell.getValue<Date>()).format('MM/DD/YYYY hh:mm A');
      },
    },
  ];
  const tableDefaults = useDefaultTableOptions<FileMetadata>(TableType.NESTED);
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: files,
    columns,
    initialState: {
      showGlobalFilter: true,
      density: 'compact',
      columnVisibility: {
        id: false,
        vin: false,
        plate: true,
        startTime: true,
        endTime: true,
        status: true,
        ancestry: false,
      },
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      isLoading,
      showLoadingOverlay: false,
      showSkeletons: false,
    },
    enableSorting: false,
    enableColumnDragging: false,
    enableColumnActions: false,
    enableRowSelection: false,
    getRowId: (row) => `${row.fileKey}`,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleRowClick(row.original),
      sx: {
        backgroundColor: 'white',
        cursor: 'pointer',
      },
    }),
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        tableType={TableType.NESTED}
        isSubmitting={isSubmitting}
        optionsDisabled={!canUpload}
        menuOptions={[
          {
            onClick: () => fileInputRef.current?.click(),
            content: t('buttons.upload', { name: t('file.label') }),
            icon: <FileUpload />,
          },
        ]}
      >
        {' '}
        <Stack
          direction={'row'}
          flexGrow={1}
          justifyContent={'flex-start'}
          alignItems={'center'}
        >
          <Typography variant='h3' textTransform={'capitalize'}>
            {t('file.label_plural', { ns: 'entities' })}
          </Typography>
        </Stack>
      </MRTableToolbar>
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => [
      <MenuItem
        key={1}
        divider
        onClick={() => {
          handleRemoveClick(row.original);
          closeMenu();
        }}
        sx={{ m: 0, backgroundColor: 'white' }}
      >
        <ListItemIcon>
          <Delete />
        </ListItemIcon>
        {t('buttons.remove', { name: t('file.label') })}
      </MenuItem>,
      <MenuItem
        key={2}
        onClick={() => handleDownloadClick(row.original)}
        sx={{ m: 0, backgroundColor: 'white' }}
      >
        <ListItemIcon>
          <Download />
        </ListItemIcon>
        {t('buttons.download', { name: t('file.label') })}
      </MenuItem>,
    ],
  });

  const itemHeight = 70;
  const renderItem = (file: FileMetadata, index: number) => {
    return (
      <ListItem
        key={file.fileKey}
        onClick={() => handleRowClick(file)}
        divider
        sx={{ backgroundColor: 'white', height: itemHeight }}
        secondaryAction={
          <ListItemMenuButton
            menuOptions={[
              {
                onClick: () => handleRemoveClick(file),
                content: t('buttons.remove', { name: t('file.label') }),
                disabled: !canUpload,
              },
              {
                onClick: () => handleDownloadClick(file),
                content: t('buttons.download', { name: t('file.label') }),
              },
            ]}
          />
        }
      >
        <ListItemText
          primary={file.data.title}
          secondary={file.fileType}
          primaryTypographyProps={{
            noWrap: true,
            variant: 'subtitle1',
          }}
          secondaryTypographyProps={{ noWrap: true, variant: 'subtitle2' }}
        />
      </ListItem>
    );
  };

  return (
    <React.Fragment>
      <input
        type='file'
        ref={fileInputRef}
        onChange={handleFileUpload}
        style={{ display: 'none' }}
        multiple
      />
      {selected && (
        <PhotoViewer
          open={!!selected}
          close={handleCloseImage}
          image={selected}
        />
      )}
      {isMobile && (
        <MobilePanelList<FileMetadata>
          data={files}
          columns={columns.map((c) => ({
            name: c.header,
            searchKey: c.accessorKey,
          }))}
          itemHeight={itemHeight}
          withSearchBar
          heightOffset={TAB_LAYOUT_HEADER_HEIGHT}
          title={t('file.label_plural', { ns: 'entities' })}
          renderItem={renderItem}
          menuOptions={[
            {
              onClick: () => fileInputRef.current?.click(),
              content: t('buttons.upload', {
                name: t('file.label', { ns: 'entities' }),
              }),
              icon: <FileUpload />,
              disabled: !canUpload,
            },
          ]}
        />
      )}
      {!isMobile && (
        <FormFrame loading={isLoading}>
          <MaterialReactTable table={table} />
        </FormFrame>
      )}
    </React.Fragment>
  );
};
